import { useInfiniteQuery, useQuery } from '@tanstack/react-query'
import { useRef, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import type { ApiError } from '@/api/errors'
import type { CallerResponse, CallerStatus, Paginated } from '@/api/types'
import { queryKeys } from '@/api/queryKeys'
import { useDebounced } from '@/lib/useDebounced'
import { Badge } from '@/components/Badge'
import { Banner } from '@/components/Banner'
import { Card } from '@/components/Card'
import { Page, PageHeader } from '@/components/Page'
import { EmptyState } from '@/components/EmptyState'
import { Icon } from '@/components/Icon'
import { SkeletonList } from '@/components/Skeleton'
import { Spinner } from '@/components/Spinner'
import { formatDaysLate, formatVisitDate } from '@/lib/dates'
import { cx } from '@/lib/cx'
import { useInfiniteScroll } from '@/lib/useInfiniteScroll'
import { callers as api, CALLER_STATUS_META } from './api'
import { Button } from '@/components/Button'
import { useModals } from '@/app/modals/ModalContext'
import { errorMessage } from '@/api/messages'

const PAGE = 25

/** "All" first: the list is a worklist, and a filter that hides the work by
 *  default is a filter nobody trusts. */
const FILTERS: { value: CallerStatus | 'all'; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'new_call', label: 'New calls' },
  { value: 'follow_up', label: 'Follow-ups' },
  { value: 'appointment_booked', label: 'Booked' },
  { value: 'became_patient', label: 'Became patients' },
  { value: 'no_appointment', label: 'No appointment' },
  { value: 'cancelled', label: 'Cancelled' },
]

/** Kept apart from patients on purpose: most calls never become one, and an OP
 *  number for someone who never walked in is a record of nothing. */
export function CallersScreen() {
  const modals = useModals()
  const [params, setParams] = useSearchParams()
  const status = (params.get('status') as CallerStatus | 'all' | null) ?? 'all'
  const [typed, setTyped] = useState('')
  const q = useDebounced(typed.trim())
  const sentinel = useRef<HTMLDivElement>(null)

  const list = useInfiniteQuery<Paginated<CallerResponse>, ApiError>({
    queryKey: queryKeys.callers(status, q),
    initialPageParam: null as string | null,
    queryFn: ({ pageParam, signal }) =>
      api.list(status, q, PAGE, pageParam as string | null, signal),
    getNextPageParam: (last) => last.next_cursor ?? undefined,
  })
  const counts = useQuery<{ counts: Record<string, number> }, ApiError>({
    queryKey: queryKeys.callerCounts,
    queryFn: ({ signal }) => api.counts(signal),
  })

  const rows = list.data?.pages.flatMap((page) => page.data) ?? []

  useInfiniteScroll(sentinel, {
    hasMore: Boolean(list.hasNextPage),
    loading: list.isFetchingNextPage,
    onLoadMore: () => void list.fetchNextPage(),
  })

  const logCall = (
    <Button onClick={() => modals.open({ kind: 'caller' })}>
      <Icon name="plus" />
      Log a call
    </Button>
  )

  return (
    <Page fill action={logCall}>
      <PageHeader
        eyebrow="Booking"
        title="Callers"
        description="Enquiry calls, and who still needs ringing back."
        primary={logCall}
      />

      {list.error && (
        <Banner tone={list.error.isNetwork ? 'warning' : 'danger'}>{errorMessage(list.error)}</Banner>
      )}

      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap gap-2" role="group" aria-label="Filter by status">
          {FILTERS.map((filter) => {
            const active = filter.value === status
            const count =
              filter.value === 'all'
                ? Object.values(counts.data?.counts ?? {}).reduce((a, b) => a + b, 0)
                : (counts.data?.counts[filter.value] ?? 0)
            return (
              <button
                key={filter.value}
                type="button"
                className={cx(
                  'inline-flex min-h-touch items-center gap-2 rounded-full border px-3 text-sm transition-colors duration-150 ease-standard',
                  active
                    ? 'border-accent bg-accent font-semibold text-on-accent'
                    : 'border-line-soft bg-surface text-fg-muted hover:border-line hover:text-fg',
                )}
                aria-pressed={active}
                onClick={() =>
                  setParams(filter.value === 'all' ? {} : { status: filter.value })
                }
              >
                {filter.label}
                {counts.data && <span className="text-xs tabular-nums">{count}</span>}
              </button>
            )
          })}
        </div>

        <label className="flex h-field max-w-88 flex-1 basis-56 items-center gap-2 rounded-md border border-line-soft bg-surface px-3 text-fg-subtle focus-within:border-accent [&_input]:min-w-0 [&_input]:flex-1 [&_input]:self-stretch [&_input]:border-0 [&_input]:bg-transparent [&_input]:text-fg [&_input]:outline-none">
          <span className="sr-only">Search callers by name or phone number</span>
          <Icon name="search" size="1rem" />
          <input
            type="search"
            value={typed}
            placeholder="Name or phone number…"
            onChange={(event) => setTyped(event.target.value)}
            autoComplete="off"
          />
        </label>
      </div>

      <Card
        title={q ? 'Search results' : (FILTERS.find((f) => f.value === status)?.label ?? 'All')}
        flush
        fill
      >
        {list.isLoading ? (
          <div className="p-5">
            <SkeletonList rows={4} label="Loading callers" />
          </div>
        ) : rows.length === 0 ? (
          <EmptyState
            icon={<Icon name="phone" size="2rem" />}
            title={q ? 'No enquiry matches that' : 'No calls logged yet'}
            body={
              q
                ? 'Try the number they rang from, or part of the name.'
                : 'Log the next enquiry and it will be waiting here when they call back.'
            }
          />
        ) : (
          <>
          <ul className="m-0 list-none p-0 [&>li+li]:border-t [&>li+li]:border-line-soft">
            {rows.map((caller) => (
              <li key={caller.id}>
                <Link className="group flex min-h-15 items-center gap-3 px-4 py-3 text-fg no-underline transition-colors duration-150 ease-standard hover:bg-hover max-md:flex-wrap max-md:gap-y-2" to={`/callers/${caller.id}`}>
                  <span className="flex min-w-0 flex-1 flex-col max-md:order-1 max-md:flex-[1_1_0]">
                    <span className="flex min-w-0 items-baseline gap-2">
                      <span className="truncate font-semibold">{caller.full_name}</span>
                      {caller.phone && <span className="text-xs whitespace-nowrap text-fg-subtle tabular-nums">{caller.phone}</span>}
                    </span>
                    <span className="truncate text-sm text-fg-muted">
                      {[
                        caller.place,
                        `Called ${formatVisitDate(caller.call_date)}`,
                        caller.requested_appointment_date
                          ? `Wants ${formatVisitDate(caller.requested_appointment_date)}`
                          : null,
                      ]
                        .filter(Boolean)
                        .join(' · ')}
                    </span>
                  </span>

                  <span className="flex flex-wrap justify-end gap-2 max-md:order-3 max-md:flex-[1_1_100%] max-md:justify-start">
                    {/* Words, never a colour alone: "6 days late" is readable to
                        anyone, including a colour-blind reader. */}
                    {caller.days_overdue > 0 && (
                      <Badge tone="warning">{formatDaysLate(caller.days_overdue)}</Badge>
                    )}
                    <Badge tone={CALLER_STATUS_META[caller.status].tone}>
                      {CALLER_STATUS_META[caller.status].label}
                    </Badge>
                  </span>
                  <span className="flex-none text-fg-subtle transition-transform duration-150 ease-standard group-hover:translate-x-0.5 group-hover:text-accent max-md:order-2" aria-hidden="true">
                    <Icon name="chevronRight" size="1rem" />
                  </span>
                </Link>
              </li>
            ))}
          </ul>

          {/* Watched, not clicked: crossing it loads the next page. */}
          <div ref={sentinel} className="flex min-h-10 items-center justify-center p-3">
            {list.isFetchingNextPage && <Spinner label="Loading more enquiries" />}
            {!list.hasNextPage && rows.length > PAGE && (
              <p className="text-xs text-fg-subtle">That is every enquiry.</p>
            )}
          </div>
        </>
        )}
      </Card>
    </Page>
  )
}
